import { useMemo } from "react";
import { Operation, CreatorRole } from "../models/operations";
import { StateEnveloped } from "../redux/helpers";
import { makeHookDeprecationWarn } from "../helpers/warnings";

import {
  useOperationsData,
  useCreateOperation,
  useUpdateOperation,
  useDeleteOperation,
  useOperationsGlobalActivity,
  useOperationsGlobalStatus,
} from "../next/hooks";

export type Data = {
  operations: Operation[];
  operationsById: Record<string, Operation | undefined>;
};
export type OperationsHookResult = StateEnveloped<Data> & {
  modifiers: {
    add: (
      operationName: string,
      creatorRole?: CreatorRole,
    ) => Promise<Operation>;
    update: (operation: Operation) => Promise<void>;
    delete: (operation: Operation) => Promise<void>;
  };
};
export type OperationsHook = () => OperationsHookResult;

const deprecationWarn = makeHookDeprecationWarn("useOperations");

/**
 * @deprecated Use next.hooks.useOperations instead.
 */
const Hook: OperationsHook = () => {
  deprecationWarn();

  const data = useOperationsData();

  const status = useOperationsGlobalStatus();
  const activity = useOperationsGlobalActivity();

  const { mutate: createOperation } = useCreateOperation();
  const { mutate: updateOperation } = useUpdateOperation();
  const { mutate: deleteOperation } = useDeleteOperation();

  const modifiers = useMemo(
    () => ({
      add: (
        operationName: string,
        creatorRole: CreatorRole = "administrator",
      ) => createOperation({ operationName, creatorRole }),
      update: async (operation: Operation) => {
        await updateOperation(operation);
      },
      delete: async (operation: Operation) => {
        await deleteOperation(operation.id);
      },
    }),
    [createOperation, updateOperation, deleteOperation],
  );

  const memoizedResult = useMemo(
    () => ({
      status,
      activity,
      data,
      modifiers,
    }),
    [status, activity, data, modifiers],
  );

  return memoizedResult;
};

export default Hook;
